import React, { useState } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Pagination,
  IconButton,
  Stack,
  LinearProgress,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Close as CloseIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  AccessTime as AccessTimeIcon,
  Business as BusinessIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Description as CsvIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import { Campaign } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { CampaignFilterService } from '../services/campaignFilterService';
import { exportCampaignPerformanceToCSV, exportCampaignPerformanceToExcel, exportCampaignPerformanceToPDF } from '../utils/exportUtils';
import moment from 'moment-timezone';
import ShareReportDialog from '../components/ShareReportDialog';
import SearchableSelect from '../components/SearchableSelect';

interface ReportFilters {
  startDate: string;
  endDate: string;
  campaignId: string;
  page: number;
  limit: number;
}

interface ReportResponse {
  success: boolean;
  data: any[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const CampaignPerformanceReportPage: React.FC = () => {
  const { user } = useAuth();
  const [filters, setFilters] = useState<ReportFilters>({
    startDate: moment().subtract(2, 'weeks').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
    campaignId: '',
    page: 1,
    limit: 25,
  });
  const [showFilters, setShowFilters] = useState(true);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  // Check if user can share reports (supervisor and above)
  const canShareReports = ['supervisor', 'manager', 'administrator', 'super_user'].includes(user?.role || '');
  const [sortBy, setSortBy] = useState<'campaign'|'department'|'employees'|'clockIns'|'clockOuts'|'avgWork'|'attendance'|'productivity'>('attendance');
  const [sortDir, setSortDir] = useState<'asc'|'desc'>('desc');

  // Fetch campaigns for filter dropdown
  const { data: campaignsData } = useQuery<{ data: { success: boolean; data: Campaign[] } }, Error>({
    queryKey: ['campaigns', filters.startDate, filters.endDate],
    queryFn: () => apiClient.campaigns.getAll({
      includeWithActivityFrom: filters.startDate,
      includeWithActivityTo: filters.endDate,
    }),
    enabled: ['team_lead', 'supervisor', 'manager', 'administrator', 'super_user'].includes(user?.role || ''),
  });

  // Filter campaigns based on user's assigned campaigns
  const availableCampaigns = campaignsData?.data?.data ? 
    CampaignFilterService.getUserAccessibleCampaigns(user!, campaignsData.data.data) : [];

  // Get report data
  const { data: reportResponse, isLoading, error, refetch } = useQuery<ReportResponse>({
    queryKey: ['campaignPerformanceReport', filters],
    queryFn: async () => {
      const response = await apiClient.timeEntries.getCampaignPerformance(filters);
      return response.data;
    },
    enabled: true,
  });

  const handleFilterChange = (field: keyof ReportFilters, value: string | number) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: field !== 'page' ? 1 : (typeof value === 'number' ? value : prev.page),
    }));
  };

  const handleSearch = () => {
    refetch();
  };

  const handleResetFilters = () => {
    setFilters({
      startDate: moment().subtract(2, 'weeks').format('YYYY-MM-DD'),
      endDate: moment().format('YYYY-MM-DD'),
      campaignId: '',
      page: 1,
      limit: 25,
    });
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    handleFilterChange('page', value);
  };

  const getPerformanceColor = (score: number) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  const getAttendanceColor = (rate: number) => {
    if (rate >= 90) return 'success';
    if (rate >= 70) return 'warning';
    return 'error';
  };

  const handleExportMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const fetchAllCampaignPerformanceForExport = async () => {
    const baseParams = {
      startDate: filters.startDate,
      endDate: filters.endDate,
      campaignId: filters.campaignId || undefined,
      page: 1,
      limit: 1000,
    };

    let allRecords: any[] = [];
    let page = 1;
    let total = 0;

    while (true) {
      const response = await apiClient.timeEntries.getCampaignPerformance({
        ...baseParams,
        page
      });
      const pageData = response.data.data || [];
      const pagination = response.data.pagination;
      allRecords = allRecords.concat(pageData);
      total = pagination?.total ?? allRecords.length;

      if (allRecords.length >= total || pageData.length === 0) {
        break;
      }
      page += 1;
    }

    return allRecords;
  };

  const handleExportCSV = async () => {
    try {
      const data = await fetchAllCampaignPerformanceForExport();
      exportCampaignPerformanceToCSV(data, filters);
    } catch (error) {
      console.error('Error exporting campaign performance CSV:', error);
      alert('Failed to export CSV. Please try again.');
    } finally {
      handleExportMenuClose();
    }
  };

  const handleExportExcel = async () => {
    try {
      const data = await fetchAllCampaignPerformanceForExport();
      exportCampaignPerformanceToExcel(data, filters);
    } catch (error) {
      console.error('Error exporting campaign performance Excel:', error);
      alert('Failed to export Excel. Please try again.');
    } finally {
      handleExportMenuClose();
    }
  };

  const handleExportPDF = async () => {
    try {
      const data = await fetchAllCampaignPerformanceForExport();
      exportCampaignPerformanceToPDF(data, filters);
    } catch (error) {
      console.error('Error exporting campaign performance PDF:', error);
      alert('Failed to export PDF. Please try again.');
    } finally {
      handleExportMenuClose();
    }
  };

  const toggleSort = (col:'campaign'|'department'|'employees'|'clockIns'|'clockOuts'|'avgWork'|'attendance'|'productivity') => {
    if (sortBy===col) setSortDir(d=>d==='asc'?'desc':'asc');
    else { setSortBy(col); setSortDir(['attendance','productivity'].includes(col)?'desc':'asc'); }
  };

  const sortedCampaigns = React.useMemo(()=>{
    const list = reportResponse?.data ? [...reportResponse.data] : [];
    list.sort((a:any,b:any)=>{
      let av:any=''; let bv:any='';
      switch (sortBy){
        case 'campaign': av=(a.campaign?.name||'').toLowerCase(); bv=(b.campaign?.name||'').toLowerCase(); break;
        case 'department': av=(a.department||'').toLowerCase(); bv=(b.department||'').toLowerCase(); break;
        case 'employees': av=a.totalEmployees||0; bv=b.totalEmployees||0; break;
        case 'clockIns': av=a.totalClockIns||0; bv=b.totalClockIns||0; break;
        case 'clockOuts': av=a.totalClockOuts||0; bv=b.totalClockOuts||0; break;
        case 'avgWork': av=a.averageWorkTime||0; bv=b.averageWorkTime||0; break;
        case 'productivity': av=a.productivityScore||0; bv=b.productivityScore||0; break;
        case 'attendance':
        default:
          av=a.attendanceRate||0; bv=b.attendanceRate||0; break;
      }
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir==='asc'?cmp:-cmp;
    });
    return list;
  },[reportResponse?.data, sortBy, sortDir]);

  const activeFilters = Object.entries(filters).filter(([key, value]) => 
    value !== '' && value !== 0 && key !== 'page' && key !== 'limit'
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Campaign Performance Report
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Filters</Typography>
            <IconButton onClick={() => setShowFilters(!showFilters)}>
              <FilterIcon />
            </IconButton>
          </Box>

          {showFilters && (
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Start Date"
                  type="date"
                  fullWidth
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="End Date"
                  type="date"
                  fullWidth
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <SearchableSelect
                  label="Campaign"
                  value={filters.campaignId}
                  onChange={(value) => handleFilterChange('campaignId', String(value))}
                  options={availableCampaigns.map((c: Campaign) => ({
                    value: c.id,
                    label: c.name,
                  }))}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <SearchableSelect
                  label="Records per page"
                  value={filters.limit}
                  onChange={(value) => handleFilterChange('limit', value as number)}
                  options={[10, 25, 50, 100].map((value) => ({
                    value,
                    label: String(value),
                  }))}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={handleResetFilters}
                  >
                    Reset to 2 Weeks
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<SearchIcon />}
                    onClick={handleSearch}
                    disabled={isLoading}
                  >
                    Search
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    endIcon={<ArrowDropDownIcon />}
                    onClick={handleExportMenuOpen}
                    disabled={!reportResponse?.data || reportResponse.data.length === 0}
                  >
                    Export
                  </Button>
                  {canShareReports && (
                    <Button
                      variant="outlined"
                      startIcon={<EmailIcon />}
                      onClick={() => setShareDialogOpen(true)}
                      disabled={!reportResponse?.data || reportResponse.data.length === 0}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      Share
                    </Button>
                  )}
                </Stack>
              </Grid>
            </Grid>
          )}

          {activeFilters.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Active Filters:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {activeFilters.map(([key, value]) => (
                  <Chip
                    key={key}
                    label={`${key}: ${value}`}
                    onDelete={() => handleFilterChange(key as keyof ReportFilters, '')}
                    deleteIcon={<CloseIcon />}
                  />
                ))}
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Export Menu */}
      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={handleExportMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem onClick={handleExportCSV}>
          <ListItemIcon>
            <CsvIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export to CSV</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleExportExcel}>
          <ListItemIcon>
            <ExcelIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export to Excel</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleExportPDF}>
          <ListItemIcon>
            <PdfIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export to PDF</ListItemText>
        </MenuItem>
      </Menu>

      {/* Results */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Campaign Performance
              {reportResponse?.pagination && (
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  ({reportResponse.pagination.total} total campaigns)
                </Typography>
              )}
            </Typography>
          </Box>

          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to load campaign performance report
            </Alert>
          )}

          {reportResponse && !isLoading && (
            <>
              <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{cursor:'pointer'}} onClick={()=>toggleSort('campaign')}>Campaign {sortBy==='campaign'?(sortDir==='asc'?'▲':'▼'):''}</TableCell>
                      <TableCell sx={{cursor:'pointer'}} onClick={()=>toggleSort('department')}>Department {sortBy==='department'?(sortDir==='asc'?'▲':'▼'):''}</TableCell>
                      <TableCell sx={{cursor:'pointer'}} onClick={()=>toggleSort('employees')}>Employees {sortBy==='employees'?(sortDir==='asc'?'▲':'▼'):''}</TableCell>
                      <TableCell sx={{cursor:'pointer'}} onClick={()=>toggleSort('clockIns')}>Clock Ins {sortBy==='clockIns'?(sortDir==='asc'?'▲':'▼'):''}</TableCell>
                      <TableCell sx={{cursor:'pointer'}} onClick={()=>toggleSort('clockOuts')}>Clock Outs {sortBy==='clockOuts'?(sortDir==='asc'?'▲':'▼'):''}</TableCell>
                      <Tooltip 
                        title="Average Work Time: Average hours worked per employee in this campaign. Work time is calculated as the time between clock-in and clock-out, with all break periods (break_start to break_end) and lunch periods (lunch_start to lunch_end) subtracted from the total."
                        arrow
                        placement="top"
                      >
                        <TableCell sx={{cursor:'pointer'}} onClick={()=>toggleSort('avgWork')}>
                          Avg Work Time {sortBy==='avgWork'?(sortDir==='asc'?'▲':'▼'):''}
                        </TableCell>
                      </Tooltip>
                      <Tooltip 
                        title="Attendance Rate: Percentage of expected clock-ins that actually occurred. Calculated as (Actual Clock-ins / Expected Clock-ins) × 100, where Expected Clock-ins = Number of Employees × Number of Days in Date Range."
                        arrow
                        placement="top"
                      >
                        <TableCell sx={{cursor:'pointer'}} onClick={()=>toggleSort('attendance')}>
                          Attendance Rate {sortBy==='attendance'?(sortDir==='asc'?'▲':'▼'):''}
                        </TableCell>
                      </Tooltip>
                      <Tooltip 
                        title="Productivity Score: Percentage of productive time vs total time. Calculated as work time divided by (work time + break time + lunch time). Higher scores indicate more time spent working vs taking breaks."
                        arrow
                        placement="top"
                      >
                        <TableCell sx={{cursor:'pointer'}} onClick={()=>toggleSort('productivity')}>
                          Productivity Score {sortBy==='productivity'?(sortDir==='asc'?'▲':'▼'):''}
                        </TableCell>
                      </Tooltip>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedCampaigns.map((campaign: any, index: number) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {campaign.campaign?.name || 'N/A'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {campaign.campaign?.description || 'No description'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={campaign.department || 'N/A'}
                            color="info"
                            size="small"
                            icon={<BusinessIcon />}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={campaign.totalEmployees}
                            color="primary"
                            size="small"
                            icon={<PeopleIcon />}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={campaign.totalClockIns}
                            color="success"
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={campaign.totalClockOuts}
                            color="error"
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip 
                            title={`Average Work Time: ${campaign.averageWorkTime} hours per employee. This is calculated from clock-in to clock-out times, with all break periods and lunch periods subtracted.`}
                            arrow
                            placement="top"
                          >
                            <Chip
                              label={`${campaign.averageWorkTime}h`}
                              color="info"
                              size="small"
                              icon={<AccessTimeIcon />}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Tooltip 
                            title={`Attendance Rate: ${campaign.attendanceRate}%. This represents ${campaign.totalClockIns} actual clock-ins out of an expected ${campaign.totalEmployees * (reportResponse?.pagination ? Math.ceil((new Date(filters.endDate).getTime() - new Date(filters.startDate).getTime()) / (1000 * 60 * 60 * 24)) + 1 : 1)} clock-ins (${campaign.totalEmployees} employees × number of days in date range).`}
                            arrow
                            placement="top"
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip
                                label={`${campaign.attendanceRate}%`}
                                color={getAttendanceColor(campaign.attendanceRate)}
                                size="small"
                              />
                              <LinearProgress
                                variant="determinate"
                                value={campaign.attendanceRate}
                                color={getAttendanceColor(campaign.attendanceRate)}
                                sx={{ width: 60, height: 6 }}
                              />
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Tooltip 
                            title={`Productivity Score: ${campaign.productivityScore}%. This represents the percentage of time spent working vs taking breaks and lunch. Calculated as work time / (work time + break time + lunch time) × 100.`}
                            arrow
                            placement="top"
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip
                                label={`${campaign.productivityScore}%`}
                                color={getPerformanceColor(campaign.productivityScore)}
                                size="small"
                                icon={<TrendingUpIcon />}
                              />
                              <LinearProgress
                                variant="determinate"
                                value={campaign.productivityScore}
                                color={getPerformanceColor(campaign.productivityScore)}
                                sx={{ width: 60, height: 6 }}
                              />
                            </Box>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              {reportResponse.pagination.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={reportResponse.pagination.totalPages}
                    page={reportResponse.pagination.page}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </>
          )}

          {reportResponse && reportResponse.data.length === 0 && !isLoading && (
            <Alert severity="info">
              No campaign performance data found matching the selected criteria.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Share Report Dialog */}
      {canShareReports && (
        <ShareReportDialog
          open={shareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
          reportType="campaign-performance"
          reportFilters={filters}
          reportTitle="Campaign Performance Report"
          onShareSuccess={() => {
            console.log('Report shared successfully');
          }}
        />
      )}
    </Container>
  );
};

export default CampaignPerformanceReportPage;
